import React, { useMemo } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { collection } from 'firebase/firestore'
import { useCollectionDataOnce } from 'react-firebase-hooks/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { summarizeScheduleWindows } from '../../../common/backtestSchedule'
import { useAlertContext } from '../contexts/AlertContext'
import { useStandardAlertTemplates } from '../hooks/useStandardAlertTemplates'

export default function StandardAlertTemplatesTable() {
  const { tradingHouse } = useApplicationSuiteContext()
  const { templates, loading, error } = useStandardAlertTemplates()
  const { openTemplateSidePanel } = useAlertContext()

  // One-time read, not a live listener — this is just a static id→name lookup for
  // display, not data the table needs to react to in real time. Not filtered by
  // `active` either — a template can reference a pattern that's since been
  // deactivated, and we still need to resolve its name for display here.
  const [standardPatterns] = useCollectionDataOnce<{ id: string; name: string }>(
    collection(firebase_firestore, `tradingHouses/${tradingHouse}/standardPatterns`) as any
  )

  const patternNameById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const pattern of standardPatterns || []) {
      map[pattern.id] = pattern.name
    }
    return map
  }, [standardPatterns])

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">Failed to load templates: {error.message}</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Symbol</TableCell>
              <TableCell>Pattern</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Provisioning</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                    No Starter Plus templates yet. Click "New Template" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {templates.map((template) => (
              <TableRow
                key={template.id}
                hover
                onClick={() => openTemplateSidePanel(template)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{template.name}</TableCell>
                <TableCell>{template.symbol}</TableCell>
                <TableCell>{patternNameById[template.patternId] || template.patternId}</TableCell>
                <TableCell>{summarizeScheduleWindows(template.schedule)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={template.autoProvision !== false ? 'On' : 'Off'}
                    color={template.autoProvision !== false ? 'success' : 'default'}
                    variant={template.autoProvision !== false ? 'filled' : 'outlined'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
